import { resolveConcreteClassName } from './CsInterfaceResolver';
import { scanMethodBodyCalls } from './CsCallScanner';
import { scanThirdLevelInvocations } from './CsThirdLevelCallScanner';
import outputMsgEngine from '../../../Widgets/OutputMsgWidget/Core/OutputMsgEngine';

const EVENT_HANDLER_SUFFIX = 'EventHandler';
const EVENT_CATCHER_SUFFIX = 'EventCatcher';

/**
 * Maps identifiers usable as call targets in a method body to their
 * dependency type. Covers both direct ctor-param usage (rare, no backing
 * field) AND the standard `_field = param;` ctor-injection convention —
 * scans the constructor's own bodyTokens for `IDENTIFIER = IDENTIFIER ;`
 * assignments and maps the LHS (the real field name used elsewhere, e.g.
 * `_syncHandler`) to the RHS param's dependency type.
 * @param {object} type
 * @returns {Map<string,string>}
 */
function buildFieldTypeMap(type) {
  const ctor = type.members.find(function isCtor(m) { return m.memberKind === 'constructor'; });
  const dependencies = ctor?.dependencies || [];
  const depTypeByParamName = new Map(dependencies.map(function toEntry(d) { return [d.name, d.type]; }));

  const map = new Map(depTypeByParamName);

  const bodyTokens = ctor?.bodyTokens || [];
  for (let i = 0; i < bodyTokens.length - 2; i += 1) {
    const lhs = bodyTokens[i];
    const eq = bodyTokens[i + 1];
    const rhs = bodyTokens[i + 2];
    const terminator = bodyTokens[i + 3];
    if (
      lhs?.type === 'identifier' &&
      eq?.value === '=' &&
      rhs?.type === 'identifier' &&
      terminator?.value === ';' &&
      depTypeByParamName.has(rhs.value)
    ) {
      map.set(lhs.value, depTypeByParamName.get(rhs.value));
    }
  }

  return map;
}

/**
 * Resolves scanThirdLevelInvocations' raw hits (`_field.Property.Method(...)`)
 * into the same {targetField, targetType, targetMethodName} shape
 * scanMethodBodyCalls produces, so the caller's existing forEach (concrete
 * resolution, event-boundary check, step-push, further traversal) handles
 * them without duplicating that logic — ONE arrow, straight to the resolved
 * Cache-like class, same as any other call (drawing it as a separate hop
 * through the outer dependency's own lifeline turned out wrong: the return
 * type got resolved against the WRONG participant). `targetMethodName` stays
 * the bare method name (`AddOrUpdate`) so return-type lookup and further
 * traversal keep working unchanged; `displayMethod` carries the full
 * navigation path (`Cache.AddOrUpdate`) purely for the step's label, so the
 * path is still visible on the diagram without a semantically-wrong extra
 * message. Resolution: outer field's OWN type (already concrete, e.g.
 * DataHandler) -> find its `Property` member -> parse the property's
 * declared type from its `text` (`"Cache: ICache"`, see CsMemberScanner —
 * properties have no separate structured type field) -> that becomes the
 * returned targetType, left UNRESOLVED (interface name) for the caller's own
 * resolveConcreteClassName step, same as a normal call.
 * @param {{targetField: string, targetType: string, intermediateProperty: string, targetMethodName: string}[]} thirdLevelHits
 * @param {Map<string, object>} typesByName
 * @param {Record<string,string>} overrides
 * @param {string} visitKey - for warning messages
 * @returns {{targetField: string, targetType: string, targetMethodName: string, displayMethod: string}[]}
 */
function resolveThirdLevelCalls(thirdLevelHits, typesByName, overrides, visitKey) {
  const resolved = [];
  thirdLevelHits.forEach(function resolveOne(hit) {
    const outerConcreteName = resolveConcreteClassName(hit.targetType, overrides);
    const outerType = typesByName.get(outerConcreteName);
    const propertyMember = outerType?.members.find(function sameName(m) { return m.name === hit.intermediateProperty; });
    const propertyType = propertyMember?.text.split(': ')[1];
    if (!propertyType) {
      outputMsgEngine.addMsg('CsSeqDia', 'warning', `Third-level invocation ${hit.targetField}.${hit.intermediateProperty}.${hit.targetMethodName} in ${visitKey} — property '${hit.intermediateProperty}' not found on ${outerConcreteName}, skipped`);
      return;
    }
    resolved.push({
      targetField: hit.intermediateProperty,
      targetType: propertyType,
      targetMethodName: hit.targetMethodName,
      displayMethod: `${hit.targetType}.${hit.intermediateProperty}.${hit.targetMethodName}`,
    });
  });
  return resolved;
}

/**
 * Walks the call chain starting at (entryClassName, entryMethodName) across
 * all parsed types (merge of multiple parseCsClasses() results, one per
 * file), resolving interface dependencies to concrete classes via the
 * Ixxx->xxx convention. Stops at maxDepth, at an unresolved type, or at a
 * (class, method) pair already visited (cycle guard).
 *
 * Does not traverse into event boundaries — a target class whose name ends
 * in EventHandler/EventCatcher is recorded as a step but not followed, since
 * publisher/subscriber wiring isn't visible from a single call site.
 *
 * Each entry in allParsedTypes must carry its own `.namespace` (string|null)
 * — parseCsClasses() attaches namespace once per file to the RETURNED ARRAY,
 * not per type, so the caller merging multiple files must stamp it onto
 * each type first: `parsed.forEach(t => { t.namespace = parsed.namespace; })`.
 * @param {object[]} allParsedTypes
 * @param {string} entryClassName
 * @param {string} entryMethodName
 * @param {object} [options]
 * @param {number} [options.maxDepth]
 * @param {Record<string,string>} [options.interfaceOverrides]
 * @returns {{steps: {fromClass: string, fromNamespace: string|null, toClass: string, toNamespace: string|null, method: string, boundary: boolean, depth: number, returnType: string|undefined}[], warnings: string[], entryNamespace: string|null, entryReturnType: string|undefined}}
 */
export function walkCallChain(allParsedTypes, entryClassName, entryMethodName, options = {}) {
  const maxDepth = options.maxDepth ?? 6;
  const overrides = options.interfaceOverrides ?? {};
  const typesByName = new Map(allParsedTypes.map(function toEntry(t) { return [t.name, t]; }));

  const steps = [];
  const warnings = [];
  const visited = new Set();

  function visit(className, methodName, depth) {
    if (depth > maxDepth) {
      warnings.push(`Max depth (${maxDepth}) reached at ${className}.${methodName}`);
      return;
    }
    const visitKey = `${className}.${methodName}`;
    if (visited.has(visitKey)) {
      warnings.push(`Cycle detected at ${visitKey}, stopped`);
      return;
    }
    visited.add(visitKey);

    const type = typesByName.get(className);
    if (!type) {
      warnings.push(`Type not found: ${className} (outside imported files)`);
      return;
    }
    const method = type.members.find(function sameMethod(m) { return m.name === methodName && m.memberKind === 'method'; });
    if (!method || !method.bodyTokens) {
      warnings.push(`Method body not available: ${className}.${methodName}`);
      return;
    }

    const fieldTypeMap = buildFieldTypeMap(type);
    const calls = scanMethodBodyCalls(method.bodyTokens, fieldTypeMap)
      .concat(resolveThirdLevelCalls(scanThirdLevelInvocations(method.bodyTokens, fieldTypeMap), typesByName, overrides, visitKey));

    if (calls.length === 0) {
      outputMsgEngine.addMsg('CsSeqDia', 'warning', `No calls found in ${visitKey} — check field-type resolution or empty method body`);
    }

    calls.forEach(function followCall(call) {
      const concreteName = resolveConcreteClassName(call.targetType, overrides);
      const targetType = typesByName.get(concreteName) || null;
      const isEventBoundary = concreteName.endsWith(EVENT_HANDLER_SUFFIX) || concreteName.endsWith(EVENT_CATCHER_SUFFIX);
      const targetMethod = targetType?.members.find(function sameMethod(m) { return m.name === call.targetMethodName && m.memberKind === 'method'; });

      steps.push({
        fromClass: className,
        fromNamespace: type.namespace ?? null,
        toClass: concreteName,
        toNamespace: targetType?.namespace ?? null,
        method: call.displayMethod ?? call.targetMethodName,
        boundary: isEventBoundary || !targetType,
        depth,
        returnType: targetMethod?.returnType,
        params: targetMethod?.params,
        thirdLevel: Boolean(call.displayMethod),
      });

      if (!isEventBoundary && targetType) {
        visit(concreteName, call.targetMethodName, depth + 1);
      }
    });
  }

  visit(entryClassName, entryMethodName, 0);

  // Entry point's own namespace/return type — needed by CsSeqShapeFactory to draw
  // the synthetic "found message" from an external actor into the entry method
  // (see buildSequenceShape). Looked up separately from the visit() walk above
  // since visit() may have bailed out early (type/method not found) without ever
  // reaching the point where these would otherwise be captured.
  const entryType = typesByName.get(entryClassName);
  const entryMethod = entryType?.members.find(function sameMethod(m) { return m.name === entryMethodName && m.memberKind === 'method'; });

  return { steps, warnings, entryNamespace: entryType?.namespace ?? null, entryReturnType: entryMethod?.returnType, entryParams: entryMethod?.params };
}
